import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Clock, 
  FileText, 
  CheckCircle, 
  PlayCircle, 
  Calendar,
  TrendingUp,
  BarChart3,
  ArrowLeft,
  Download,
  Eye
} from 'lucide-react';
import Header from './Header';

// Using relative URLs
const API = '/api';

const VoiceAssessmentHistory = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && currentWorkspace) {
      fetchAssessmentHistory();
      fetchStats();
    }
  }, [user, currentWorkspace]);

  const fetchAssessmentHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/voice/sessions/user/${user.id}?workspace_id=${currentWorkspace.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSessions(response.data.sessions);
    } catch (err) {
      console.error('Error fetching assessment history:', err);
      setError('Failed to load assessment history');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/voice/sessions/user/${user.id}/stats?workspace_id=${currentWorkspace.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const getFrameworkBadgeColor = (framework) => {
    const colors = {
      'SOC2_TYPE1': 'bg-blue-100 text-blue-800',
      'SOC2_TYPE2': 'bg-indigo-100 text-indigo-800',
      'ISO27001': 'bg-purple-100 text-purple-800',
      'GDPR': 'bg-green-100 text-green-800',
      'HIPAA': 'bg-red-100 text-red-800',
      'PCI_DSS': 'bg-orange-100 text-orange-800'
    };
    return colors[framework] || 'bg-gray-100 text-gray-800';
  };

  const getFrameworkDisplayName = (framework) => {
    const names = {
      'SOC2_TYPE1': 'SOC 2 Type 1',
      'SOC2_TYPE2': 'SOC 2 Type 2',
      'ISO27001': 'ISO 27001',
      'GDPR': 'GDPR',
      'HIPAA': 'HIPAA',
      'PCI_DSS': 'PCI DSS'
    };
    return names[framework] || framework;
  };

  const getStatusBadge = (session) => {
    // "analyzed" or "completed" with analysis = fully done
    if (session.status === 'analyzed' || (session.status === 'completed' && session.has_analysis)) {
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
    } else if (session.status === 'completed') {
      return <Badge className="bg-yellow-100 text-yellow-800">Pending Analysis</Badge>;
    } else if (session.status === 'active') {
      return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
    } else {
      // Fallback for any other status
      return <Badge className="bg-gray-100 text-gray-800">{session.status || 'Unknown'}</Badge>;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewReport = (sessionId) => {
    navigate(`/assessment/report/${sessionId}`);
  };

  const downloadReport = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/voice/session/${sessionId}/download-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Extract filename from Content-Disposition header
      let filename = 'Nova Assessment Report.pdf'; // fallback
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading assessment history...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-slate-900">Voice Assessment History</h1>
          <p className="text-slate-600 mt-2">View all your past voice assessments and reports</p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Assessments</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_sessions}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{stats.completed_sessions}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Time</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.total_time_formatted}</p>
                  </div>
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Avg Duration</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.average_duration_minutes.toFixed(1)}m</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Assessments List */}
        {sessions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Assessments Yet</h3>
              <p className="text-slate-600 mb-6">Start your first voice assessment to see it here</p>
              <Button onClick={() => navigate('/assessment/voice')}>
                Start New Assessment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <Card key={session.session_id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className={getFrameworkBadgeColor(session.framework)}>
                          {getFrameworkDisplayName(session.framework)}
                        </Badge>
                        {getStatusBadge(session)}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600 mb-1">Started</p>
                          <p className="font-medium text-slate-900 flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(session.started_at || session.created_at)}
                          </p>
                        </div>
                        
                        {session.completed_at && (
                          <div>
                            <p className="text-slate-600 mb-1">Completed</p>
                            <p className="font-medium text-slate-900 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" />
                              {formatDate(session.completed_at)}
                            </p>
                          </div>
                        )}
                        
                        <div>
                          <p className="text-slate-600 mb-1">Duration</p>
                          <p className="font-medium text-slate-900 flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatDuration(session.total_duration_seconds)}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-slate-600 mb-1">Conversation</p>
                          <p className="font-medium text-slate-900 flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            {session.conversation_turns_count} turns
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      {session.has_analysis && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewReport(session.session_id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Report
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadReport(session.session_id)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {session.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/assessment/voice')}
                        >
                          <PlayCircle className="h-4 w-4 mr-2" />
                          Resume
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAssessmentHistory;
